import { getJobTitleChineseName } from "@utils/constants";

import { isFrontendVisibilityOff } from "@helpers/employeeFrontendVisibility";
import { UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID } from "./appointmentUnassignedConsultingRoom";
/** 新增／編輯預約表單：僅「目前已選且之後才封存」的選項後綴 */
export const ARCHIVED_DOCTOR_SELECT_SUFFIX = "(已封存，請改選其他)";

/** 後端員工清單中「系統不指定醫師」常見名稱（僅比對 DOCTOR） */
export const SYSTEM_UNASSIGNED_DOCTOR_NAMES = [
  "不指定醫師",
  "未指定醫師",
  "不選擇醫師",
  "未選擇醫師",
  "不指定醫生",
  "未指定醫生",
  "不選擇醫生",
  "未選擇醫生",
  "不指定",
  "未指定",
  "不選擇",
  "未選擇",
];

/**
 * 後端員工清單中「系統不指定諮詢師」常見名稱
 * （後端多為「不指定」；顯示層改寫為「不指定諮詢師」）
 */
export const SYSTEM_UNASSIGNED_COUNSELLOR_NAMES = [
  "不指定",
  "不指定諮詢師",
  "不指定咨詢師",
];

/** 與刻意同名真實選項區隔：系統不指定項 hover／active 用專案紅底 */
export const APPOINTMENT_SELECT_SENTINEL_OPTION_CLASS =
  "appointment-select-option--sentinel";

/** @deprecated 舊假選項 value；改為使用清單內真實員工 id */
export const UNASSIGNED_DOCTOR_SELECT_VALUE = "不指定";
export const UNASSIGNED_DOCTOR_SELECT_LABEL = "不指定醫師";
export const UNASSIGNED_COUNSELLOR_SELECT_VALUE = "不指定";
export const UNASSIGNED_COUNSELLOR_SELECT_LABEL = "不指定諮詢師";

/** Smart Scheduler 若依 label 排序，「不指定…」在 zh-Hant 會靠前；加此前綴確保排最後 */
export const UNASSIGNED_DOCTOR_SORT_PREFIX = "\uFFFF";

/** Scheduler 自訂 sort 僅能讀 id/label/color；用 label 前綴表示「有／無預約」順序 */
export const SCHEDULER_WITH_APPOINTMENTS_SORT_PREFIX = "\uE010";
export const SCHEDULER_WITHOUT_APPOINTMENTS_SORT_PREFIX = "\uE011";

export function stripSchedulerAppointmentSortPrefix(name) {
  const value = String(name ?? "");
  if (value.startsWith(SCHEDULER_WITH_APPOINTMENTS_SORT_PREFIX)) {
    return value.slice(SCHEDULER_WITH_APPOINTMENTS_SORT_PREFIX.length);
  }
  if (value.startsWith(SCHEDULER_WITHOUT_APPOINTMENTS_SORT_PREFIX)) {
    return value.slice(SCHEDULER_WITHOUT_APPOINTMENTS_SORT_PREFIX.length);
  }
  return value;
}

export function stripSchedulerResourceDisplayLabel(name) {
  return stripUnassignedDoctorSortPrefix(
    stripSchedulerAppointmentSortPrefix(name)
  );
}

export function stripUnassignedDoctorSortPrefix(name) {
  const value = String(name ?? "");
  return value.startsWith(UNASSIGNED_DOCTOR_SORT_PREFIX)
    ? value.slice(UNASSIGNED_DOCTOR_SORT_PREFIX.length)
    : value;
}

export function normalizeSystemUnassignedDoctorName(name) {
  return stripUnassignedDoctorSortPrefix(name)
    .normalize("NFKC")
    .replace(/[\s\u3000]+/g, "")
    .trim();
}

export function isSystemUnassignedDoctorName(name) {
  const normalized = normalizeSystemUnassignedDoctorName(name);
  if (!normalized) return false;
  if (
    SYSTEM_UNASSIGNED_DOCTOR_NAMES.some(
      (candidate) => normalizeSystemUnassignedDoctorName(candidate) === normalized
    )
  ) {
    return true;
  }
  // 涵蓋「不／未」+「指定／選擇」+ 可選「醫師／醫生」
  return /^(不|未)(指定|選擇)(醫師|醫生)?$/.test(normalized);
}

/** 員工姓名不再加排序前綴。虛擬隨機欄自己加前綴。 */
export function applyUnassignedDoctorSortPrefix(name) {
  return String(name ?? "").trim();
}

export function isSystemUnassignedCounsellorName(name) {
  const normalized = String(name ?? "").trim();
  return SYSTEM_UNASSIGNED_COUNSELLOR_NAMES.includes(normalized);
}

/** 顯示層：後端「不指定」等 →「不指定諮詢師」 */
export function formatCounsellorDisplayName(name) {
  const normalized = String(name ?? "").trim();
  if (!normalized) return "";
  if (isSystemUnassignedCounsellorName(normalized)) {
    return UNASSIGNED_COUNSELLOR_SELECT_LABEL;
  }
  return normalized;
}

/** 隨機配醫師不是某一位員工。姓名叫「不指定醫師」的人仍是普通醫師。 */
export function findSystemUnassignedDoctor(_employees = []) {
  return undefined;
}

/** 只有虛擬隨機欄（id -1），不含姓名剛好叫「不指定醫師」的員工 */
export function isUnassignedDoctorResourceItem(item, _employees = []) {
  if (!item) return false;
  const id = Number(item.id ?? item.value ?? item.employee_id);
  return Number.isFinite(id) && id === -1;
}

/** 預約表醫師欄資源：不指定醫師（含虛擬欄）一律移到最後 */
export function orderDoctorResourcesUnassignedLast(items = [], employees = []) {
  const normal = [];
  const unassigned = [];
  (items || []).forEach((item) => {
    if (isUnassignedDoctorResourceItem(item, employees)) {
      unassigned.push(item);
    } else {
      normal.push(item);
    }
  });
  return [...normal, ...unassigned];
}

export function isUnassignedConsultingRoomResourceItem(item) {
  return (
    Number(item?.id ?? item?.value) === UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID
  );
}

export function isTrailingSchedulerGroupResource(
  item,
  employees = [],
  groupValue = "doctorId"
) {
  if (groupValue === "consulting_room_id") {
    return isUnassignedConsultingRoomResourceItem(item);
  }
  return isUnassignedDoctorResourceItem(item, employees);
}

function resolveResourceDisplayBaseLabel(item) {
  return stripSchedulerResourceDisplayLabel(
    item?.label ?? item?.doctorName ?? item?.name ?? item?.text ?? ""
  );
}

function applySchedulerAppointmentSortPrefix(baseLabel, hasAppointments) {
  const base = String(baseLabel ?? "").trim();
  if (!base) return base;
  return hasAppointments
    ? `${SCHEDULER_WITH_APPOINTMENTS_SORT_PREFIX}${base}`
    : `${SCHEDULER_WITHOUT_APPOINTMENTS_SORT_PREFIX}${base}`;
}

/** 有預約的醫師／診間在前（含未指定），無預約在後 */
export function orderSchedulerResourcesByAppointments(
  items = [],
  idsWithAppointments = new Set(),
  { employees = [], groupValue = "doctorId" } = {}
) {
  const withAppointments = [];
  const withoutAppointments = [];

  (items || []).forEach((item) => {
    const id = Number(item.id ?? item.value);
    if (idsWithAppointments.has(id)) {
      withAppointments.push(item);
    } else {
      withoutAppointments.push(item);
    }
  });

  return [...withAppointments, ...withoutAppointments];
}

/** 寫入 label 排序前綴（Scheduler sort 後只剩 id/label/color） */
export function stampSchedulerResourceAppointmentSortLabels(
  items = [],
  idsWithAppointments = new Set(),
  employees = [],
  groupValue = "doctorId",
  { onlyResourcesWithAppointments = false } = {}
) {
  return (items || []).map((item) => {
    const baseLabel = resolveResourceDisplayBaseLabel(item);
    const id = Number(item.id ?? item.value);
    const hasAppointments = onlyResourcesWithAppointments
      ? true
      : idsWithAppointments.has(id);
    const label = applySchedulerAppointmentSortPrefix(
      baseLabel,
      hasAppointments
    );

    return {
      ...item,
      label,
      doctorName: baseLabel,
      name: baseLabel,
      text: baseLabel,
    };
  });
}

function resolveSchedulerResourceSortRank(item) {
  const label = String(item?.label ?? "");
  if (label.startsWith(SCHEDULER_WITH_APPOINTMENTS_SORT_PREFIX)) return 0;
  if (label.startsWith(SCHEDULER_WITHOUT_APPOINTMENTS_SORT_PREFIX)) return 1;
  return 1;
}

function isTrailingSchedulerResourceSortItem(item) {
  const label = item?.label ?? item?.doctorName ?? item?.name ?? "";
  return (
    Number(item?.id) === -1 ||
    Number(item?.id) === UNASSIGNED_CONSULTING_ROOM_RESOURCE_ID ||
    String(label).startsWith(UNASSIGNED_DOCTOR_SORT_PREFIX)
  );
}

/**
 * Smart Scheduler 會把 dataSource 轉成 `{ [id]: {id,label,color} }` 再 Object.values，
 * 只保留 id/label/color，且變成依數字 id 排序。必須設 sortBy（truthy）+ custom sort，
 * 才能在僅有 label 的資料上把「不指定醫師」推到最後。
 */
export function compareSchedulerDoctorResourceLast(a, b) {
  const aRank = resolveSchedulerResourceSortRank(a);
  const bRank = resolveSchedulerResourceSortRank(b);
  if (aRank !== bRank) return aRank - bRank;

  // 同為「無預約」時，未指定醫師／診間仍排在其他無預約欄之後
  if (aRank === 1) {
    const aTrailing = isTrailingSchedulerResourceSortItem(a);
    const bTrailing = isTrailingSchedulerResourceSortItem(b);
    if (aTrailing !== bTrailing) return aTrailing ? 1 : -1;
  }

  return 0;
}

const SCHEDULER_GROUP_RESOURCE_VALUES = new Set([
  "doctorId",
  "consulting_room_id",
]);

export function withSchedulerResourceSortOrder(resources = []) {
  return (resources || []).map((resource) => {
    if (!resource || !Array.isArray(resource.dataSource)) return resource;
    if (!SCHEDULER_GROUP_RESOURCE_VALUES.has(resource.value)) return resource;
    return {
      ...resource,
      // sortBy 必填：Scheduler _applyResourceSorting 在 !sortBy 時直接 return
      sortBy: "label",
      sortOrder: "custom",
      sortFunction: compareSchedulerDoctorResourceLast,
      dataSource: resource.dataSource.map((item) => ({
        ...item,
        label:
          item.label ??
          item.doctorName ??
          item.name ??
          item.text ??
          "",
      })),
    };
  });
}

export function compareDoctorsUnassignedLast(a, b) {
  const aUnassigned = isUnassignedDoctorResourceItem(a);
  const bUnassigned = isUnassignedDoctorResourceItem(b);
  if (aUnassigned !== bUnassigned) return aUnassigned ? 1 : -1;
  return 0;
}

/** 隨機「不指定醫師」先隱藏，下拉不提供這一項。 */
export function needsFakeUnassignedDoctorOption(_employees = []) {
  return false;
}

export function isFakeUnassignedDoctorSelectValue(doctorId) {
  return (
    doctorId === UNASSIGNED_DOCTOR_SELECT_VALUE ||
    doctorId === UNASSIGNED_DOCTOR_SELECT_LABEL
  );
}

export function buildFakeUnassignedDoctorSelectOption() {
  return {
    value: UNASSIGNED_DOCTOR_SELECT_VALUE,
    label: UNASSIGNED_DOCTOR_SELECT_LABEL,
    className: APPOINTMENT_SELECT_SENTINEL_OPTION_CLASS,
    doctorName: UNASSIGNED_DOCTOR_SELECT_LABEL,
  };
}

export function findSystemUnassignedCounsellor(employees = []) {
  return (employees || []).find(
    (emp) =>
      emp?.job_title === "COUNSELLOR" &&
      isSystemUnassignedCounsellorName(emp?.name)
  );
}

/** 隨機不是員工 id。選到姓名叫「不指定醫師」的人，仍然是指定該醫師。 */
export function isSystemUnassignedDoctorId(_employees = [], _doctorId) {
  return false;
}

export function isSystemUnassignedCounsellorId(employees = [], counsellorId) {
  if (
    counsellorId == null ||
    counsellorId === "" ||
    counsellorId === UNASSIGNED_COUNSELLOR_SELECT_VALUE
  ) {
    return false;
  }
  const emp = (employees || []).find(
    (e) => Number(e.id) === Number(counsellorId)
  );
  return isSystemUnassignedCounsellorName(emp?.name);
}

export function formatArchivedDoctorSelectLabel(name, isArchived) {
  const base = String(name ?? "").trim();
  if (!isArchived || !base) return base;
  if (base.includes(ARCHIVED_DOCTOR_SELECT_SUFFIX)) return base;
  return `${base} ${ARCHIVED_DOCTOR_SELECT_SUFFIX}`;
}

function normalizeKeepSelectedId(keepSelectedId) {
  if (
    keepSelectedId == null ||
    keepSelectedId === "" ||
    keepSelectedId === UNASSIGNED_DOCTOR_SELECT_VALUE
  ) {
    return null;
  }
  const n = Number(keepSelectedId);
  return Number.isFinite(n) ? n : null;
}

function isKeepSelectedEmployee(emp, keepSelectedId) {
  const keepId = normalizeKeepSelectedId(keepSelectedId);
  if (keepId == null) return false;
  return Number(emp?.id) === keepId;
}

/** 分店醫師（預設不含已封存／關閉前台顯示；可保留目前已選） */
export function getAppointmentFormDoctors(employees = [], { keepSelectedId } = {}) {
  return (employees || []).filter((emp) => {
    if (emp?.job_title !== "DOCTOR") return false;
    if (isKeepSelectedEmployee(emp, keepSelectedId)) return true;
    if (emp?.is_archived) return false;
    if (isFrontendVisibilityOff(emp)) return false;
    return true;
  });
}

/** 分店諮詢師（預設不含已封存／關閉前台顯示；可保留目前已選） */
export function getAppointmentFormCounsellors(
  employees = [],
  { keepSelectedId } = {}
) {
  return (employees || []).filter((emp) => {
    if (emp?.job_title !== "COUNSELLOR") return false;
    if (isKeepSelectedEmployee(emp, keepSelectedId)) return true;
    if (emp?.is_archived) return false;
    if (isFrontendVisibilityOff(emp)) return false;
    return true;
  });
}

function mapStaffToSelectOptions(
  staffList = [],
  { markSystemUnassigned, resolveDisplayName } = {}
) {
  return staffList.map((emp) => {
    const isSystemUnassigned = markSystemUnassigned
      ? markSystemUnassigned(emp)
      : false;
    const displayName = resolveDisplayName
      ? resolveDisplayName(emp, isSystemUnassigned)
      : emp.name;
    return {
      value: emp.id,
      label: formatArchivedDoctorSelectLabel(displayName, emp.is_archived),
      isArchived: !!emp.is_archived,
      doctorName: emp.name,
      className: isSystemUnassigned
        ? APPOINTMENT_SELECT_SENTINEL_OPTION_CLASS
        : emp.is_archived
          ? "appointment-doctor-select-option--archived"
          : undefined,
    };
  });
}

/** 系統不指定項置頂（仍使用後端真實 id） */
function pinSystemUnassignedOptions(options = []) {
  const pinned = [];
  const rest = [];
  options.forEach((opt) => {
    if (opt.className === APPOINTMENT_SELECT_SENTINEL_OPTION_CLASS) {
      pinned.push(opt);
    } else {
      rest.push(opt);
    }
  });
  return [...pinned, ...rest];
}

/**
 * 醫師下拉只列團隊醫師的真實姓名。
 * 隨機「不指定醫師」先隱藏，不出現在清單。
 */
export function buildAppointmentDoctorSelectOptions(
  employees = [],
  { keepSelectedId } = {}
) {
  const doctors = mapStaffToSelectOptions(
    getAppointmentFormDoctors(employees, { keepSelectedId })
  );
  if (!needsFakeUnassignedDoctorOption(employees)) return doctors;
  return pinSystemUnassignedOptions([
    buildFakeUnassignedDoctorSelectOption(),
    ...doctors,
  ]);
}

function normalizeSelectedServiceIds(serviceIds = []) {
  return (Array.isArray(serviceIds) ? serviceIds : [serviceIds])
    .filter((id) => id != null && id !== "")
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
}

/** 員工是否提供所有已選服務（id 一律 Number，避免 string/number 比對失敗） */
export function employeeProvidesAllSelectedServices(employee, serviceIds = []) {
  const selectedServiceIds = normalizeSelectedServiceIds(serviceIds);
  if (selectedServiceIds.length === 0) return true;
  const provided = new Set(
    (employee?.services || [])
      .map((service) => Number(service?.id))
      .filter((id) => Number.isFinite(id))
  );
  return selectedServiceIds.every((id) => provided.has(id));
}

/**
 * 依已選服務篩醫師下拉。
 * - 服務 id 比對正規化為 Number
 * - 姓名叫「不指定醫師」的員工與其他醫師同一套服務規則
 * - 隨機「不指定醫師」先隱藏
 * - 無符合的醫師時 fallback 全店醫師
 */
export function buildAppointmentDoctorSelectOptionsForServices(
  employees = [],
  serviceIds = [],
  { keepSelectedId } = {}
) {
  const selectedServiceIds = normalizeSelectedServiceIds(serviceIds);
  if (selectedServiceIds.length === 0) {
    return buildAppointmentDoctorSelectOptions(employees, { keepSelectedId });
  }

  const matchedEmployees = (employees || []).filter((emp) =>
    employeeProvidesAllSelectedServices(emp, selectedServiceIds)
  );
  const hasMatchingDoctor =
    getAppointmentFormDoctors(matchedEmployees).length > 0;

  if (!hasMatchingDoctor) {
    return buildAppointmentDoctorSelectOptions(employees, { keepSelectedId });
  }

  const matchedIds = new Set(matchedEmployees.map((emp) => Number(emp.id)));
  const keepId = normalizeKeepSelectedId(keepSelectedId);
  const pool = (employees || []).filter(
    (emp) =>
      matchedIds.has(Number(emp.id)) ||
      (keepId != null && Number(emp.id) === keepId)
  );

  return buildAppointmentDoctorSelectOptions(pool, { keepSelectedId });
}

/**
 * 諮詢師下拉：從清單找出真實「不指定」置頂，顯示改為「不指定諮詢師」並紅底；不另造假選項。
 */
export function buildAppointmentCounsellorSelectOptions(
  employees = [],
  { keepSelectedId } = {}
) {
  const counsellors = mapStaffToSelectOptions(
    getAppointmentFormCounsellors(employees, { keepSelectedId }),
    {
      markSystemUnassigned: (emp) => isSystemUnassignedCounsellorName(emp?.name),
      resolveDisplayName: (emp, isSystemUnassigned) =>
        isSystemUnassigned ? UNASSIGNED_COUNSELLOR_SELECT_LABEL : emp.name,
    }
  );
  return pinSystemUnassignedOptions(counsellors);
}

/** 表單預設／清空：清單內系統不指定諮詢師 id，沒有則 null */
export function resolveDefaultCounsellorSelectValue(employees = []) {
  return findSystemUnassignedCounsellor(employees)?.id ?? null;
}

/** 表單諮詢師值 → API counsellorId（空 → null；系統不指定仍送真實 id） */
export function resolveApiCounsellorId(formCounsellorId, _employees = []) {
  if (
    formCounsellorId == null ||
    formCounsellorId === "" ||
    formCounsellorId === UNASSIGNED_COUNSELLOR_SELECT_VALUE
  ) {
    return null;
  }
  return formCounsellorId;
}

/** 編輯載入：無諮詢師時選清單內系統不指定諮詢師 */
export function resolveFormCounsellorSelectValue(counsellorId, employees = []) {
  if (
    counsellorId == null ||
    counsellorId === "" ||
    counsellorId === UNASSIGNED_COUNSELLOR_SELECT_VALUE
  ) {
    return resolveDefaultCounsellorSelectValue(employees);
  }
  const n = Number(counsellorId);
  if (!Number.isFinite(n) || n === 0) {
    return resolveDefaultCounsellorSelectValue(employees);
  }
  return counsellorId;
}

/** 選到系統不指定醫師或前端假選項時，送 isUnassigned: true */
export function resolveIsUnassignedFromDoctorId(employees, doctorId) {
  if (isFakeUnassignedDoctorSelectValue(doctorId)) return true;
  return isSystemUnassignedDoctorId(employees, doctorId);
}

/** 編輯送出：假選項無法對應真實 employee_id，保留原服務醫師 */
export function resolveSubmitEmployeeIdFromDoctorSelect({
  formEmployeeId,
  fallbackEmployeeId,
  randomEmployeeId,
}) {
  if (isFakeUnassignedDoctorSelectValue(formEmployeeId)) {
    return fallbackEmployeeId ?? randomEmployeeId ?? null;
  }
  if (
    formEmployeeId != null &&
    formEmployeeId !== "" &&
    !Number.isNaN(Number(formEmployeeId))
  ) {
    return formEmployeeId;
  }
  return randomEmployeeId || fallbackEmployeeId || null;
}

export function isArchivedAppointmentDoctor(employees, doctorId) {
  if (
    doctorId == null ||
    doctorId === "" ||
    doctorId === UNASSIGNED_DOCTOR_SELECT_VALUE
  ) {
    return false;
  }
  const emp = (employees || []).find(
    (e) => Number(e.id) === Number(doctorId)
  );
  return !!emp?.is_archived;
}

function resolveStaffEmployee(employees, employeesObj, staffId) {
  if (staffId == null || staffId === "") return null;
  const fromObj = employeesObj?.[staffId] ?? employeesObj?.[Number(staffId)];
  if (fromObj) return fromObj;
  return (
    (employees || []).find((e) => Number(e.id) === Number(staffId)) ?? null
  );
}

function formatArchivedStaffLabel(employee, fallbackRoleLabel) {
  if (!employee) return null;
  const name = String(employee.name ?? "").trim();
  if (!name) return null;
  const jobTitle =
    getJobTitleChineseName(employee.job_title) || fallbackRoleLabel;
  return `${jobTitle} ${name}`;
}

function replaceArchivedStaffInMessage(msg, roleLabel, staffLabel) {
  if (!staffLabel || msg.includes(staffLabel)) return msg;
  const prefix = `該${roleLabel}`;
  if (msg.includes(prefix)) {
    return msg.replace(prefix, staffLabel);
  }
  return msg;
}

function resolveArchivedStaffLabelFromContext({
  employees,
  employeesObj,
  counsellorId,
  employeeId,
}) {
  const doctor = resolveStaffEmployee(employees, employeesObj, employeeId);
  const counsellor = resolveStaffEmployee(employees, employeesObj, counsellorId);

  if (doctor?.is_archived) {
    return formatArchivedStaffLabel(doctor, "醫師");
  }
  if (counsellor?.is_archived) {
    return formatArchivedStaffLabel(counsellor, "諮詢師");
  }
  if (doctor) {
    return formatArchivedStaffLabel(doctor, "醫師");
  }
  if (counsellor) {
    return formatArchivedStaffLabel(counsellor, "諮詢師");
  }
  return null;
}

/** 已封存相關 API 錯誤：保留後端 msg 後段，並以「職稱 姓名」取代「該諮詢師／該醫師／該員工」 */
export function resolveAppointmentArchivedErrorMessage(
  apiMsg,
  { employees = [], employeesObj = {}, counsellorId, employeeId } = {}
) {
  const msg = String(apiMsg ?? "").trim();
  if (!msg || !msg.includes("封存")) return null;

  const context = { employees, employeesObj, counsellorId, employeeId };

  if (msg.includes("諮詢師")) {
    const employee = resolveStaffEmployee(
      employees,
      employeesObj,
      counsellorId
    );
    const staffLabel = formatArchivedStaffLabel(employee, "諮詢師");
    if (staffLabel) {
      return replaceArchivedStaffInMessage(msg, "諮詢師", staffLabel);
    }
  }

  if (msg.includes("醫師")) {
    const employee = resolveStaffEmployee(employees, employeesObj, employeeId);
    const staffLabel = formatArchivedStaffLabel(employee, "醫師");
    if (staffLabel) {
      return replaceArchivedStaffInMessage(msg, "醫師", staffLabel);
    }
  }

  if (msg.includes("醫生")) {
    const employee = resolveStaffEmployee(employees, employeesObj, employeeId);
    const staffLabel = formatArchivedStaffLabel(employee, "醫師");
    if (staffLabel) {
      return replaceArchivedStaffInMessage(msg, "醫生", staffLabel);
    }
  }

  if (msg.includes("員工")) {
    const staffLabel = resolveArchivedStaffLabelFromContext(context);
    if (staffLabel) {
      return replaceArchivedStaffInMessage(msg, "員工", staffLabel);
    }
  }

  return msg;
}

export function getAppointmentDoctorSelectClassName(
  employees,
  selectedDoctorId,
  baseClassName = "w-100"
) {
  const archived = isArchivedAppointmentDoctor(employees, selectedDoctorId);
  return archived
    ? `${baseClassName} appointment-doctor-select--archived-value`
    : baseClassName;
}

export function getAppointmentCounsellorSelectClassName(
  employees,
  selectedCounsellorId,
  baseClassName = "w-100"
) {
  return getAppointmentDoctorSelectClassName(
    employees,
    selectedCounsellorId,
    baseClassName
  );
}
